import axios from "axios";
import { baseUrl, extendHeaders, headers } from "./HelpService";
import { TClosedShipmentWithShortages, TShipmentDetails, TShipmentItem } from "../../types/shipment.type";

const PageUrl = baseUrl + 'cases/';

const getShipmentCases = (accountId: number): Promise<Response | null> => {
    return fetch(PageUrl + `getShipmentCases/?account_id=${encodeURIComponent(accountId)}`, {
        method: "GET",
        headers: extendHeaders()
    })
        .then((response: any) => response.json())
        .then((data: any) => data as Response)
        .catch(() => null);
};

const getCasesByShipmentId = async (accountId: number, shipmentId: string): Promise<Response | null> => {
    const shipmentDetailsResult = await fetch(PageUrl + `getCasesByShipmentId/?account_id=${encodeURIComponent(accountId)}&shipment_id=${encodeURIComponent(shipmentId)}`, {
        method: "GET",
        headers: extendHeaders()
    });
    return await shipmentDetailsResult.json();
};

const getOptionalCasesDetails = (accountId: number): Promise<Response | null> => {
    return fetch(PageUrl + `getOptionalCasesDetails/?account_id=${encodeURIComponent(accountId)}`, {
        method: "GET",
        headers: extendHeaders(),
    })
        .then((response: any) => response.json())
        .then((data: any) => data as Response)
        .catch(() => null);
};

const getCasesList = (queryParams: {}): Promise<Response | null> => {
    const urlParams = new URLSearchParams(queryParams);
    return fetch(PageUrl + "?" + urlParams, {
        method: "GET",
        headers: extendHeaders()
    })
        .then((response) => response.json())
        .then((data) => data as Response)
        .catch(() => null);
};

const dismissCase = (account_id, casesId): Promise<Response | null> => {
    return fetch(PageUrl + "updateCaseToDismiss/", {
        method: "PUT",
        headers: extendHeaders(),
        body: JSON.stringify({ account_id: account_id, id: casesId })
    })
        .then((response) => response.json())
        .then((data) => data as Response)
        .catch(() => null);
};

const updateCaseNumber = (account_id, pk, caseNumber): Promise<Response | null> => {
    return fetch(PageUrl + "updateCaseNumber/", {
        method: "POST",
        headers: extendHeaders(),
        body: JSON.stringify({ account_id: account_id, id: pk, case_number: caseNumber })
    })
        .then((response) => response.json())
        .then((data) => data as Response)
        .catch(() => null);
};

const updateStatusCase = (case_id, status): Promise<Response | null> => {
    return fetch(PageUrl + "updateStatusCase/", {
        method: "PUT",
        headers: extendHeaders(),
        body: JSON.stringify({ case_id: case_id, status: status })
    })
        .then((response) => response.json())
        .then((data) => data as Response)
        .catch(() => null);
};

const downloadCasesFile = (account_id) => {
    return axios({
        url: PageUrl + `downloadNewCasesFile/?account_id=${encodeURIComponent(account_id)}`,
        method: 'GET',
        headers: headers(),
        responseType: 'blob'
    }).then((response) => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `cases-${account_id.toString()}.xlsx`);
        document.body.appendChild(link);
        link.click();
    });
};

const downloadShipmentsToExcel = (account_id) => {
    return axios({
        url: PageUrl + `downloadShipmentsToExcel/?account_id=${encodeURIComponent(account_id)}`,
        method: 'GET',
        headers: headers(),
        responseType: 'blob'
    }).then((response) => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `shipments-${account_id.toString()}.xlsx`);
        document.body.appendChild(link);
        link.click();
    });
};
export const getShipmentDetailsById = async (shipmentId: string): Promise<TShipmentDetails | null> => {
    try {
        const response = await fetch(`${PageUrl}getShipmentDetails/?shipment_id=${encodeURIComponent(shipmentId)}`, {
            method: "GET",
            headers: extendHeaders()
        });

        if (!response.ok) {
            throw new Error('Failed to fetch shipment details');
        }

        const data = await response.json();
        return data as TShipmentDetails;
    } catch {
        return null;
    }
};

export const getClosedShipmentsWithShortages = async (accountId: number): Promise<TClosedShipmentWithShortages[] | null> => {
    try {
        const closedShipmentsResponse = await fetch(`${PageUrl}getClosedShipmentsWithShortages/?account_id=${encodeURIComponent(accountId)}`, {
            method: "GET",
            headers: extendHeaders(),
        });

        if (!closedShipmentsResponse.ok) {
            throw new Error('Failed to fetch closed shipments');
        }

        const closed_shipments = (await closedShipmentsResponse.json()).items;
        return closed_shipments as TClosedShipmentWithShortages[];
    } catch {
        return null;
    }
};


// Fetch items for a specific shipment ID
export const getItemsByShipmentId = async (shipmentId: string): Promise<TShipmentItem[] | null> => {
    try {
        const shipmentsItemResponse = await fetch(`${PageUrl}getItemsByShipmentId/?shipment_id=${encodeURIComponent(shipmentId)}`, {
            method: "GET",
            headers: extendHeaders(),
        });

        if (!shipmentsItemResponse.ok) {
            throw new Error('Failed to fetch items by shipment ID');
        }

        const shipment_items = (await shipmentsItemResponse.json()).items;
        return shipment_items as TShipmentItem[];

    } catch {
        return null;
    }
};

export const deleteShipment = async (accountId: number, shipmentId: string): Promise<void> => {
  const response = await fetch(`${PageUrl}deleteShipment/?account_id=${encodeURIComponent(accountId)}&shipment_id=${encodeURIComponent(shipmentId)}`, {
    method: "DELETE",
    headers: extendHeaders(),
  });

  if (!response.ok) {
    let message = "Failed to delete shipment";
    try {
      const data = await response.json();
      if (data?.message) message = data.message;
    } catch {}
    throw new Error(message);
  }
};

const CaseService = {
    getShipmentCases,
    getOptionalCasesDetails,
    getCasesList,
    dismissCase,
    updateCaseNumber,
    updateStatusCase,
    downloadCasesFile,
    downloadShipmentsToExcel,
    getCasesByShipmentId,
    getShipmentDetailsById,
    getItemsByShipmentId,
    getClosedShipmentsWithShortages,
    deleteShipment
};

export default CaseService;
